import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import StatusBadge from '../../../components/shared/StatusBadge';
import { Search, Plus, X, Pencil, Trash2, FileSpreadsheet, MessageCircle, Printer } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { printContent } from '../../../utils/print';
import { maskCPFOrCNPJ, maskPhone } from '../../../utils/masks';

const TIPOS_ENTREGA = ['Motoboy', 'Transportadora', 'Terceirizado', 'Retirada'];
const PIE_COLORS = ['#22c55e', '#BB3E18'];

function waLink(phone) {
  const d = (phone || '').replace(/\D/g, '');
  if (!d) return null;
  return `https://wa.me/${d.length <= 11 ? '55' + d : d}`;
}

export default function ClientesLista() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams] = useSearchParams();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [rotaFilter, setRotaFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [entregaFilter, setEntregaFilter] = useState('');
  const [semCompra, setSemCompra] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [rotas, setRotas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [statsCompra, setStatsCompra] = useState(null);
  const [saving, setSaving] = useState({});
  const fileInputRef = useRef(null);
  const printRef = useRef(null);

  // Carrega opções de filtro
  useEffect(() => {
    api.get('/rotas').then(r => setRotas(r.data || [])).catch(() => {});
    api.get('/configuracoes/tipos-cliente').then(r => setTipos(r.data || [])).catch(() => {});
    api.get('/clientes/stats-compra-mes').then(r => setStatsCompra(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      if (rotaFilter) params.set('rota_id', rotaFilter);
      if (tipoFilter) params.set('tipo_cliente_id', tipoFilter);
      if (entregaFilter) params.set('tipo_entrega', entregaFilter);
      if (semCompra) params.set('sem_compra_mes', 'true');
      const res = await api.get(`/clientes?${params}`);
      setClientes(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, search, rotaFilter, tipoFilter, entregaFilter, semCompra]);

  const quickUpdate = async (clienteId, field, value) => {
    setSaving(s => ({ ...s, [clienteId + field]: true }));
    try {
      await api.patch(`/clientes/${clienteId}/quick-update`, { [field]: value });
      setClientes(prev => prev.map(c => c.id === clienteId ? { ...c, [field]: value } : c));
    } catch {} finally {
      setSaving(s => ({ ...s, [clienteId + field]: false }));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/clientes/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir cliente');
    } finally { setDeleting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/clientes/importar-xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      load();
    } catch (err) {
      setImportResult({ erro: err.response?.data?.detail || 'Erro ao importar planilha' });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const pieData = statsCompra ? [
    { name: 'Compraram', value: statsCompra.compraram },
    { name: 'Não compraram', value: statsCompra.nao_compraram },
  ] : [];

  return (
    <div data-testid="clientes-lista">
      {/* Modal delete */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-dashboard font-bold text-lg text-brand-text mb-2">Confirmar exclusão</h3>
            <p className="text-sm font-body text-brand-text-muted mb-1">
              Excluir <strong className="text-brand-text">"{deleteTarget.label}"</strong>?
            </p>
            <p className="text-xs text-amber-600 font-body mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 rounded-lg border border-brand-border text-sm font-dashboard" data-testid="cancel-delete-btn">Cancelar</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-dashboard font-semibold disabled:opacity-60" data-testid="confirm-delete-btn">{deleting ? 'Excluindo...' : 'Excluir'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="dashboard-heading text-2xl">Clientes</h1>
          <p className="text-brand-text-muted font-body text-sm">{clientes.length} cliente(s)</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => printContent('Clientes', printRef)}
            disabled={clientes.length === 0}
            className="flex items-center gap-2 py-2 px-4 rounded-lg border border-brand-border text-brand-text font-dashboard font-semibold text-sm hover:bg-brand-bg transition-colors disabled:opacity-50"
            data-testid="print-clientes-btn"
          >
            <Printer className="w-4 h-4" /> Imprimir
          </button>
          {isAdmin && (
            <>
              <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleImport} data-testid="import-file-input" />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="flex items-center gap-2 py-2 px-4 rounded-lg border border-brand-primary text-brand-primary font-dashboard font-semibold text-sm hover:bg-brand-accent-light transition-colors disabled:opacity-60"
                data-testid="import-xlsx-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                {importing ? 'Importando...' : 'Importar XLSX'}
              </button>
            </>
          )}
          <Link to="/admin/clientes/novo" className="flex items-center gap-2 py-2 px-4 rounded-lg btn-primary font-dashboard font-semibold text-sm" data-testid="new-client-btn">
            <Plus className="w-4 h-4" /> Novo Cliente
          </Link>
        </div>
      </div>

      {/* Import result */}
      {importResult && (
        <div className={`rounded-xl px-4 py-3 mb-4 text-sm font-body flex items-start justify-between gap-3 ${importResult.erro ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-800'}`} data-testid="import-result">
          <div>
            {importResult.erro ? <p>{importResult.erro}</p> : (
              <>
                <p className="font-semibold">{importResult.importados} cliente(s) importado(s) · {importResult.ignorados} ignorado(s)</p>
                {importResult.erros?.length > 0 && (
                  <ul className="mt-1 text-xs list-disc list-inside text-red-600">
                    {importResult.erros.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </>
            )}
          </div>
          <button onClick={() => setImportResult(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Gráfico de Pizza + Filtros */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Pie chart */}
        {statsCompra && (
          <div className="card-dashboard" data-testid="pie-compra-mes">
            <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide mb-2">Compras — Mês Atual</p>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" paddingAngle={3}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${v} cliente(s)`} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
            <p className="text-center text-xs text-brand-text-muted font-body">{statsCompra.total_ativos} ativos no total</p>
          </div>
        )}

        {/* Filtros */}
        <div className={`card-dashboard ${statsCompra ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
            <div className="relative col-span-2 lg:col-span-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted" />
              <input
                className="w-full rounded-lg border border-brand-border pl-9 pr-3 py-2 text-sm font-body"
                placeholder="Buscar cliente..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="search-clientes"
              />
            </div>
            <select className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="filter-status">
              <option value="">Todos os status</option>
              <option>Pendente</option>
              <option>Ativo</option>
              <option>Inativo</option>
            </select>
            <select className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={rotaFilter} onChange={e => setRotaFilter(e.target.value)} data-testid="filter-rota">
              <option value="">Todas as rotas</option>
              {rotas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
            <select className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={tipoFilter} onChange={e => setTipoFilter(e.target.value)} data-testid="filter-tipo">
              <option value="">Todos os tipos</option>
              {tipos.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
            <select className="rounded-lg border border-brand-border px-3 py-2 text-sm font-body" value={entregaFilter} onChange={e => setEntregaFilter(e.target.value)} data-testid="filter-entrega">
              <option value="">Forma de entrega</option>
              {TIPOS_ENTREGA.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button
              onClick={() => setSemCompra(v => !v)}
              className={`col-span-2 lg:col-span-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-dashboard font-semibold transition-colors ${semCompra ? 'bg-brand-primary text-white border-brand-primary' : 'border-brand-border text-brand-text-muted hover:bg-brand-bg'}`}
              data-testid="filter-sem-compra"
            >
              {semCompra ? <X className="w-3.5 h-3.5" /> : null}
              Sem compra no mês
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="card-dashboard overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : clientes.length === 0 ? (
          <p className="text-center text-brand-text-muted text-sm py-12 font-body">Nenhum cliente encontrado</p>
        ) : (
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="border-b border-brand-border bg-brand-bg">
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Nome</th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">WhatsApp</th>
                <th className="py-2.5 px-3 text-xs text-brand-text-muted font-dashboard w-10"></th>
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Rota</th>
                {isAdmin && <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Tipo</th>}
                <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Status</th>
                <th className="py-2.5 px-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {clientes.map(c => {
                const wa = waLink(c.whatsapp);
                return (
                  <tr key={c.id} className="border-b border-brand-border last:border-0 hover:bg-brand-bg/50">
                    <td className="py-2 px-3">
                      <p className="text-brand-text font-medium leading-tight">{c.nome_empresa || c.nome_contato}</p>
                      {c.nome_empresa && <p className="text-xs text-brand-text-muted">{c.nome_contato}</p>}
                    </td>
                    <td className="py-2 px-3">
                      {c.whatsapp ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-brand-text-muted font-mono hidden lg:inline">{c.whatsapp}</span>
                          {wa && (
                            <a href={wa} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-dashboard font-semibold hover:bg-green-200 transition-colors"
                              data-testid="whatsapp-btn">
                              <MessageCircle className="w-3 h-3" /> Enviar
                            </a>
                          )}
                        </div>
                      ) : <span className="text-xs text-brand-text-muted">–</span>}
                    </td>

                    {/* Editar — antes do Status */}
                    <td className="py-2 px-2 text-center">
                      <Link to={`/admin/clientes/${c.id}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-brand-accent-light text-brand-primary hover:text-brand-dark transition-colors"
                        title="Editar" data-testid="edit-client-btn">
                        <Pencil className="w-3.5 h-3.5" />
                      </Link>
                    </td>

                    {/* Rota — dropdown inline */}
                    <td className="py-2 px-3">
                      <div className="relative">
                        <select
                          value={c.rota_id || ''}
                          onChange={e => quickUpdate(c.id, 'rota_id', e.target.value)}
                          disabled={saving[c.id + 'rota_id']}
                          className="rounded border border-brand-border text-xs font-body px-2 py-1 pr-6 bg-white focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50 max-w-[130px]"
                          data-testid="inline-rota-select"
                        >
                          <option value="">— Rota —</option>
                          {rotas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                        </select>
                        {saving[c.id + 'rota_id'] && (
                          <span className="absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
                        )}
                      </div>
                    </td>

                    {/* Tipo — dropdown inline (apenas admin) */}
                    {isAdmin && (
                      <td className="py-2 px-3">
                        <div className="relative">
                          <select
                            value={c.tipo_cliente_id || ''}
                            onChange={e => quickUpdate(c.id, 'tipo_cliente_id', e.target.value)}
                            disabled={saving[c.id + 'tipo_cliente_id']}
                            className="rounded border border-brand-border text-xs font-body px-2 py-1 pr-6 bg-white focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50 max-w-[130px]"
                            data-testid="inline-tipo-select"
                          >
                            <option value="">— Tipo —</option>
                            {tipos.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                          </select>
                          {saving[c.id + 'tipo_cliente_id'] && (
                            <span className="absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
                          )}
                        </div>
                      </td>
                    )}

                    <td className="py-2 px-3"><StatusBadge status={c.status} /></td>

                    <td className="py-2 px-2 text-center">
                      {isAdmin && (
                        <button
                          onClick={() => setDeleteTarget({ id: c.id, label: c.nome_empresa || c.nome_contato })}
                          className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                          data-testid="delete-cliente-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Tabela somente leitura, usada apenas para impressão (printContent lê este ref) */}
      <div ref={printRef} className="hidden">
        <div className="mb-4 pb-3 border-b-2 border-brand-dark">
          <h2 className="font-dashboard font-bold text-xl text-brand-text">Fábrica de Pães</h2>
          <p className="text-sm font-body text-brand-text-muted">Lista de Clientes</p>
          <p className="text-xs font-body text-brand-text-muted">
            Gerado em {new Date().toLocaleDateString('pt-BR')} · {clientes.length} cliente(s)
          </p>
        </div>
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="border-b-2 border-brand-dark">
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">Empresa / Contato</th>
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">CPF/CNPJ</th>
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">WhatsApp</th>
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">Cidade/UF</th>
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">Rota</th>
              {isAdmin && <th className="text-left py-1.5 px-2 text-xs font-dashboard">Tipo</th>}
              <th className="text-left py-1.5 px-2 text-xs font-dashboard">Status</th>
            </tr>
          </thead>
          <tbody>
            {clientes.map(c => (
              <tr key={c.id} className="border-b border-brand-border">
                <td className="py-1.5 px-2">
                  <p className="font-semibold text-brand-text">{c.nome_empresa || c.nome_contato}</p>
                  {c.nome_empresa && <p className="text-xs text-brand-text-muted">{c.nome_contato}</p>}
                </td>
                <td className="py-1.5 px-2">{c.cpf_cnpj ? maskCPFOrCNPJ(c.cpf_cnpj) : '–'}</td>
                <td className="py-1.5 px-2">{c.whatsapp ? maskPhone(c.whatsapp) : '–'}</td>
                <td className="py-1.5 px-2">{[c.cidade, c.uf].filter(Boolean).join('/') || '–'}</td>
                <td className="py-1.5 px-2">{c.rota_nome || '–'}</td>
                {isAdmin && <td className="py-1.5 px-2">{c.tipo_cliente_nome || '–'}</td>}
                <td className="py-1.5 px-2">{c.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
